import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";
import { lockBodyScroll } from "../utils/scrollLock";
import { useFocusTrap } from "../utils/focusTrap";

// A popup for a short form or a list: a bottom sheet on phones, a centred card
// on wider screens. The title and a close button stay at the top and the
// action buttons (`footer`) at the bottom while the middle scrolls, so Save is
// always in reach — including with the on-screen keyboard open.
//
//   <Sheet title="Edit profile" onClose={cancel} busy={saving} footer={<Buttons />}>
//     …fields…
//   </Sheet>
//
// `busy` stops the popup being closed (backdrop tap, ✕, Escape) while something
// is being saved.
export default function Sheet({ title, onClose, busy = false, footer, children }) {
  const backdropRef = useRef(null);
  const panelRef = useRef(null);
  const visibleBox = useVisibleViewport();
  useFocusTrap(panelRef, true);

  useEffect(() => {
    const unlockScroll = lockBodyScroll();
    function onKeyDown(e) {
      if (e.key === "Escape" && !busy) onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      unlockScroll();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, busy]);

  // A swipe on the dimmed area or the title bar must not drag the page (and the
  // popup with it) around behind the keyboard — only the scrolling middle and
  // the text fields take touch movement.
  useEffect(() => {
    const el = backdropRef.current;
    if (!el) return undefined;
    function blockPageDrag(e) {
      if (e.target.closest && e.target.closest(".sheet-body, textarea, input")) return;
      e.preventDefault();
    }
    el.addEventListener("touchmove", blockPageDrag, { passive: false });
    return () => el.removeEventListener("touchmove", blockPageDrag);
  }, []);

  return createPortal(
    <div
      className="sheet-backdrop"
      ref={backdropRef}
      onClick={e => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
      style={visibleBox ? { top: visibleBox.top, bottom: "auto", height: visibleBox.height } : undefined}
    >
      <style>{css}</style>
      <div className="sheet-panel" ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title}>
        <div className="sheet-handle" />
        <div className="sheet-header">
          <h3 className="sheet-title">{title}</h3>
          <button type="button" className="sheet-close" onClick={onClose} disabled={busy} aria-label="Close">
            <IoClose />
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        {footer && <div className="sheet-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

// With the keyboard open, phone browsers keep the page at its full size and
// only shrink the part you can see (the "visual viewport"), dragging it around
// when you swipe. Returns { top, height } of that visible part while it differs
// from the full page, and null otherwise — so the popup fits above the
// keyboard and behaves as normal when there is none.
function useVisibleViewport() {
  const [box, setBox] = useState(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;

    function update() {
      if (vv.scale > 1.01) {
        setBox(null);
        return;
      }
      const differs = Math.abs(vv.height - window.innerHeight) > 1 || vv.offsetTop > 1;
      setBox(prev => {
        if (!differs) return prev === null ? prev : null;
        if (prev && prev.top === vv.offsetTop && prev.height === vv.height) return prev;
        return { top: vv.offsetTop, height: vv.height };
      });
    }

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return box;
}

const css = `
  .sheet-backdrop {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(20, 14, 8, 0.55);
  }
  .sheet-panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 480px;
    max-height: 90%;
    background: var(--bg-surface);
    border-radius: 20px 20px 0 0;
    box-shadow: 0 -8px 30px rgba(0, 0, 0, 0.22);
    animation: sheetRise 0.2s ease-out;
  }
  .sheet-handle {
    width: 40px;
    height: 4px;
    margin: 10px auto 0;
    border-radius: 2px;
    background: var(--border-input);
    flex-shrink: 0;
  }
  .sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 16px 10px 20px;
    border-bottom: 1px solid var(--border-soft);
    flex-shrink: 0;
  }
  .sheet-title { margin: 0; font-size: 17px; font-weight: 800; color: var(--text-heading); }
  .sheet-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 50%;
    background: var(--bg-soft);
    color: var(--text-secondary);
    font-size: 20px;
    cursor: pointer;
  }
  .sheet-close:disabled { opacity: 0.5; cursor: default; }
  .sheet-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 6px 20px 18px;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
  .sheet-footer {
    padding: 12px 20px calc(14px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--border-soft);
    flex-shrink: 0;
  }
  @media (min-width: 641px) {
    .sheet-backdrop { align-items: center; }
    .sheet-panel { max-width: 440px; max-height: 86%; border-radius: 20px; animation: sheetPop 0.18s ease-out; }
    .sheet-handle { display: none; }
    .sheet-footer { padding-bottom: 14px; }
  }
  @keyframes sheetRise { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @keyframes sheetPop { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
`;
